import { liveQuery } from 'dexie';
import type { LeitourDatabase } from '../database/db';
import { compareOutboxQueueOrder } from './localOutbox';
import type { SyncOutboxRecord } from './types';

type TimerHandle = ReturnType<typeof globalThis.setTimeout>;

export interface SyncOutboxWakeOptions {
  db: LeitourDatabase;
  synchronize(): Promise<void>;
  now?: () => number;
  isOnline?: () => boolean;
  setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle) => void;
}

const MAX_TIMER_DELAY_MS = 2_147_483_647;

const activeHead = async (db: LeitourDatabase): Promise<SyncOutboxRecord | undefined> =>
  (await db.syncOutbox.toArray())
    .filter(({ status }) => status !== 'superseded')
    .sort(compareOutboxQueueOrder)[0];

const immediateAt = (record: SyncOutboxRecord, now: number): boolean => {
  if (record.status === 'awaiting_bootstrap' || record.status === 'sending') return true;
  if (record.status !== 'pending') return false;
  const dueAt = Date.parse(record.nextAttemptAt);
  return Number.isFinite(dueAt) && dueAt <= now;
};

export class SyncOutboxWakeCoordinator {
  private readonly db: LeitourDatabase;
  private readonly synchronize: () => Promise<void>;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;
  private readonly setTimer: (callback: () => void, delayMs: number) => TimerHandle;
  private readonly clearTimer: (handle: TimerHandle) => void;
  private subscription: { unsubscribe(): void } | undefined;
  private wakeTimer: TimerHandle | undefined;
  private pumpPromise: Promise<void> | undefined;
  private scanRequested = false;
  private forceRequested = false;
  private synchronizing = false;
  private started = false;

  constructor(options: SyncOutboxWakeOptions) {
    this.db = options.db;
    this.synchronize = options.synchronize;
    this.now = options.now ?? Date.now;
    this.isOnline = options.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false);
    this.setTimer = options.setTimer ?? ((callback, delayMs) => globalThis.setTimeout(callback, delayMs));
    this.clearTimer = options.clearTimer ?? ((handle) => globalThis.clearTimeout(handle));
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.subscription = liveQuery(() => activeHead(this.db)).subscribe({
      next: () => { void this.checkOutbox(); },
      error: () => undefined
    });
  }

  stop(): void {
    this.started = false;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.cancelWakeTimer();
    this.scanRequested = false;
    this.forceRequested = false;
  }

  checkOutbox(): Promise<void> {
    if (!this.started) return Promise.resolve();
    this.scanRequested = true;
    return this.schedulePump();
  }

  runNow(): Promise<void> {
    if (!this.started) return Promise.resolve();
    if (this.synchronizing) this.scanRequested = true;
    else this.forceRequested = true;
    return this.schedulePump();
  }

  private schedulePump(): Promise<void> {
    if (!this.pumpPromise) {
      this.pumpPromise = Promise.resolve()
        .then(() => this.pump())
        .finally(() => {
          this.pumpPromise = undefined;
          if (this.started && (this.scanRequested || this.forceRequested)) void this.schedulePump();
        });
    }
    return this.pumpPromise;
  }

  private async pump(): Promise<void> {
    while (this.started && (this.scanRequested || this.forceRequested)) {
      const force = this.forceRequested;
      this.forceRequested = false;
      this.scanRequested = false;

      const head = await activeHead(this.db);
      this.configureWakeTimer(head);
      if (!force && (!head || !immediateAt(head, this.now()) || !this.isOnline())) continue;

      this.synchronizing = true;
      try {
        await this.synchronize();
      } catch {
        // SyncService persists retry/error state. A future outbox change or fallback will rescan it.
      } finally {
        this.synchronizing = false;
      }

      const currentHead = await activeHead(this.db);
      this.configureWakeTimer(currentHead);
    }
  }

  private configureWakeTimer(record: SyncOutboxRecord | undefined): void {
    this.cancelWakeTimer();
    if (!record || record.status !== 'pending') return;
    const dueAt = Date.parse(record.nextAttemptAt);
    const delayMs = dueAt - this.now();
    if (!Number.isFinite(delayMs) || delayMs <= 0) return;
    this.wakeTimer = this.setTimer(() => {
      this.wakeTimer = undefined;
      void this.checkOutbox();
    }, Math.min(delayMs, MAX_TIMER_DELAY_MS));
  }

  private cancelWakeTimer(): void {
    if (this.wakeTimer === undefined) return;
    this.clearTimer(this.wakeTimer);
    this.wakeTimer = undefined;
  }
}
